import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { User, Plus, Loader2 } from 'lucide-react';

const POSITIONS = ['GK', 'CB', 'LB', 'RB', 'CDM', 'CM', 'CAM', 'LW', 'RW', 'ST', 'CF'];

export default function Players() {
  const { user } = useAuth();
  const canWrite = String(user?.role || '').toLowerCase() !== 'viewer';
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [playerInsights, setPlayerInsights] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', position: 'CM', number: '', team_id: '' });

  const load = () => {
    setLoading(true);
    Promise.all([api.getPlayers(), api.getTeams(), api.getReports()])
      .then(([p, t, r]) => {
        const loadedPlayers = p.players || [];
        setPlayers(loadedPlayers);
        setTeams(t.teams || []);
        const reviews = (r.reports || []).flatMap((report) => {
          const findings = report.analysis_engine_findings || {};
          return Array.isArray(findings.scoredReviews) ? findings.scoredReviews : [];
        });
        const byName = {};
        for (const player of loadedPlayers) {
          const normalized = String(player.name || '').trim().toLowerCase();
          const rows = reviews.filter((rr) => String(rr.player_name || '').trim().toLowerCase() === normalized);
          const avgRisk = rows.length ? rows.reduce((sum, rr) => sum + Number(rr.riskScore || 0), 0) / rows.length : 0;
          byName[player.id] = {
            reviewCount: rows.length,
            recurringStrengths: rows.filter(rr => Number(rr.riskScore || 0) <= 2).map(rr => rr.positive_contribution || rr.notes || 'Reliable role execution').slice(0, 3),
            recurringWeaknesses: rows.filter(rr => Number(rr.riskScore || 0) >= 4).map(rr => rr.coach_concern || rr.classification || 'Role review concern').slice(0, 3),
            roleConsistency: rows.length ? (avgRisk <= 3 ? 'High' : avgRisk <= 6 ? 'Medium' : 'Low') : 'Not enough evidence',
            decisionQuality: rows.length ? (avgRisk <= 3 ? 'Strong' : avgRisk <= 6 ? 'Review' : 'Concern') : 'Not enough evidence',
            defensiveReliability: rows.some(rr => /defen|duel|press/i.test(`${rr.position_or_role || ''} ${rr.coach_concern || ''}`)) ? (avgRisk <= 4 ? 'Stable' : 'Needs review') : 'Not enough evidence',
            attackingContribution: rows.some(rr => /attack|wing|striker|forward|chance/i.test(`${rr.position_or_role || ''} ${rr.positive_contribution || ''}`)) ? (avgRisk <= 4 ? 'Positive' : 'Inconsistent') : 'Not enough evidence',
            improvementTrend: rows.length >= 2 ? (Number(rows[0].riskScore || 0) < Number(rows[rows.length - 1].riskScore || 0) ? 'Improving' : Number(rows[0].riskScore || 0) > Number(rows[rows.length - 1].riskScore || 0) ? 'Declining' : 'Stable') : 'Baseline',
            coachNotes: rows.map(rr => rr.notes || rr.coach_concern).filter(Boolean).slice(0, 3),
            seasonEvolution: rows.length ? `${rows.length} role review${rows.length === 1 ? '' : 's'} across report history.` : 'No report-backed role reviews yet.'
          };
        }
        setPlayerInsights(byName);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      await api.createPlayer({ ...form, number: form.number ? Number(form.number) : null });
      setForm({ name: '', position: 'CM', number: '', team_id: '' });
      setShowForm(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-navy-900">Players</h1>
          <p className="text-gray-500 mt-1">Track your player roster</p>
        </div>
        {canWrite && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-navy-900 hover:bg-navy-800 text-white px-4 py-2.5 rounded-xl transition-colors font-medium text-sm"
          >
            <Plus className="w-4 h-4" /> Add Player
          </button>
        )}
      </div>

      {canWrite && showForm && (
        <div className="bg-white rounded-2xl border border-navy-200 p-6 mb-6">
          <h2 className="text-navy-900 font-semibold mb-4">New Player</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-500 text-sm mb-1 block">Player Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600 text-sm" />
            </div>
            <div>
              <label className="text-gray-500 text-sm mb-1 block">Position</label>
              <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-navy-900 focus:outline-none focus:border-navy-600 text-sm">
                {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-gray-500 text-sm mb-1 block">Jersey Number</label>
              <input type="number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="#" className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600 text-sm" />
            </div>
            <div>
              <label className="text-gray-500 text-sm mb-1 block">Team</label>
              <select value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-navy-900 focus:outline-none focus:border-navy-600 text-sm">
                <option value="">No team</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} disabled={saving} className="bg-navy-900 hover:bg-navy-800 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Player
            </button>
            <button onClick={() => setShowForm(false)} className="bg-gray-100 hover:bg-gray-600 text-navy-900 px-5 py-2 rounded-lg text-sm transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-500" /></div>
      ) : players.length === 0 ? (
        <div className="text-center py-16">
          <User className="text-gray-700 w-12 h-12 mx-auto mb-4" />
          <p className="text-gray-500">No players added yet. Build your roster!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {players.map((player) => {
            const insight = playerInsights[player.id] || {};
            return (
            <div key={player.id} className="bg-white border border-gray-200 rounded-2xl p-5 hover:border-navy-300 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-sm">#{player.number || '?'}</span>
                </div>
                <h3 className="text-navy-900 font-semibold text-sm">{player.name}</h3>
              </div>
              <span className="bg-navy-100 text-navy-700 text-xs font-bold px-2.5 py-1 rounded-full">{player.position}</span>
              <div className="mt-4 border-t border-gray-100 pt-4 space-y-2 text-xs">
                <div className="grid grid-cols-2 gap-2">
                  <div><p className="text-gray-400 font-semibold">Role Consistency</p><p className="text-navy-900">{insight.roleConsistency || 'Not enough evidence'}</p></div>
                  <div><p className="text-gray-400 font-semibold">Decision Quality</p><p className="text-navy-900">{insight.decisionQuality || 'Not enough evidence'}</p></div>
                  <div><p className="text-gray-400 font-semibold">Defensive Reliability</p><p className="text-navy-900">{insight.defensiveReliability || 'Not enough evidence'}</p></div>
                  <div><p className="text-gray-400 font-semibold">Attacking Contribution</p><p className="text-navy-900">{insight.attackingContribution || 'Not enough evidence'}</p></div>
                </div>
                <div><p className="text-gray-400 font-semibold">Improvement Trend</p><p className="text-navy-900">{insight.improvementTrend || 'Baseline'}</p></div>
                <div><p className="text-gray-400 font-semibold">Recurring Strengths</p><p className="text-gray-600">{(insight.recurringStrengths || []).join(' · ') || 'Not enough evidence'}</p></div>
                <div><p className="text-gray-400 font-semibold">Recurring Weaknesses</p><p className="text-gray-600">{(insight.recurringWeaknesses || []).join(' · ') || 'Not enough evidence'}</p></div>
                <div><p className="text-gray-400 font-semibold">Coach Notes</p><p className="text-gray-600">{(insight.coachNotes || []).join(' · ') || 'No report-backed notes yet'}</p></div>
                <div><p className="text-gray-400 font-semibold">Season Evolution</p><p className="text-gray-600">{insight.seasonEvolution || 'No report-backed role reviews yet.'}</p></div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
